import React from "react";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { ImageMetadata } from "../types";

interface GuidingQualityDistributionChartProps {
  imagesForView: ImageMetadata[];
}

const GuidingQualityDistributionChart: React.FC<
  GuidingQualityDistributionChartProps
> = ({ imagesForView }) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      borderRadius: 3,
      p: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      minHeight: 350,
    }}
  >
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}
    >
      Guiding Quality Distribution
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Distribution of guiding error magnitudes
    </Typography>
    {imagesForView.length === 0 ? (
      <Typography color="text.secondary">No data available.</Typography>
    ) : (
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          {(() => {
            // Categorize guiding error
            const categories = [
              {
                name: 'Excellent (<0.4")',
                color: "#4caf50",
                test: (v: number) => v < 0.4,
              },
              {
                name: 'Good (0.4"-0.75")',
                color: "#4fc3f7",
                test: (v: number) => v >= 0.4 && v < 0.75,
              },
              {
                name: 'Fair (0.75"-1.0")',
                color: "#ffeb3b",
                test: (v: number) => v >= 0.75 && v < 1.0,
              },
              {
                name: 'Poor (1.0"-1.5")',
                color: "#ff8a65",
                test: (v: number) => v >= 1.0 && v < 1.5,
              },
              {
                name: 'Abort (>1.5")',
                color: "#d32f2f",
                test: (v: number) => v >= 1.5,
              },
            ];
            const counts = categories.map((cat) => ({
              ...cat,
              value: imagesForView.filter((img) =>
                cat.test(img.GuidingRMSArcSec)
              ).length,
            }));
            return (
              <Pie
                data={counts}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, value }) =>
                  value > 0 ? `${name} (${value})` : ""
                }
              >
                {counts.map((cat, i) => (
                  <Cell key={cat.name} fill={cat.color} />
                ))}
              </Pie>
            );
          })()}
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const { name, value } = payload[0].payload;
              return (
                <div
                  style={{
                    background: "#222",
                    border: "1px solid #444",
                    color: "#fff",
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  <div>{name}</div>
                  <div>Count: {value}</div>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    )}
  </Box>
);

export default GuidingQualityDistributionChart;
